/**
 * Complaints.jsx: BM-H3370 (1 Oct 2026). The Complaints Manager's page.
 *
 * Complaint Management Policy (POL-18 v1.2) §6.5 and §6.6: acknowledge within
 * 72 hours; aim for a substantive response within 21 days, updating the
 * complainant if not; resolve within 8 weeks. The written response is recorded
 * against the contents §6.5 requires, including the fixed ADR statement.
 * Server: /api/compliance/complaints (COLP, MLCO or a director).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";

function authHeaders() {
  const id = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
  return id ? { "X-Bonsai-User-Id": id } : {};
}

const BASE = `${API}/compliance/complaints`;
const INPUT = "w-full px-2 py-1 rounded-md text-sm bg-slate-900/60 border border-slate-700/60 text-slate-100";
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "");
const STATUS = { open: "Open", reopened: "Reopened", with_leo: "With the Legal Ombudsman", closed: "Closed" };

function Due({ label, iso, done }) {
  const overdue = !done && iso && new Date(iso) < new Date();
  return (
    <div className={`text-xs ${done ? "text-emerald-300" : overdue ? "text-red-300" : "text-slate-300"}`}>
      {label}: {done ? `done ${when(done)}` : `${overdue ? "overdue since" : "by"} ${when(iso)}`}
    </div>
  );
}

function RespondForm({ c, checklist, adr, onDone }) {
  const [ticks, setTicks] = useState({});
  const [justified, setJustified] = useState("");
  const [outcome, setOutcome] = useState("");
  const [remedy, setRemedy] = useState("");
  const [link, setLink] = useState("");
  const [error, setError] = useState("");
  const submit = async () => {
    setError("");
    try {
      await axios.post(`${BASE}/${c.reference}/respond`, {
        checklist: ticks, justified: justified === "" ? null : justified === "yes", outcome, remedy, letter_link: link,
      }, { headers: authHeaders() });
      onDone();
    } catch (err) {
      setError(errorMessage(err, "The response could not be recorded"));
    }
  };
  return (
    <div className="space-y-2 p-3 rounded-md border border-slate-700/60" data-testid={`respond-${c.reference}`}>
      <div className="text-xs text-slate-300">The response letter must:</div>
      {checklist.map((item) => (
        <label key={item.key} className="flex items-start gap-2 text-xs text-slate-200">
          <input type="checkbox" id={`chk-${c.reference}-${item.key}`} checked={!!ticks[item.key]}
            onChange={(e) => setTicks({ ...ticks, [item.key]: e.target.checked })} />
          {item.label}
        </label>
      ))}
      <details className="text-xs text-slate-400"><summary>The ADR statement (use word for word)</summary>
        <p className="mt-1 italic">{adr}</p></details>
      <select className={INPUT} value={justified} onChange={(e) => setJustified(e.target.value)} id={`just-${c.reference}`}>
        <option value="">Was the complaint justified?</option>
        <option value="yes">Justified</option><option value="no">Not justified</option>
      </select>
      <textarea className={INPUT} rows={2} placeholder="The outcome" value={outcome} onChange={(e) => setOutcome(e.target.value)}
        id={`outcome-${c.reference}`} />
      <input className={INPUT} placeholder="Remedy offered (if any)" value={remedy} onChange={(e) => setRemedy(e.target.value)} />
      <input className={INPUT} placeholder="Link to the response letter in SharePoint" value={link} onChange={(e) => setLink(e.target.value)} />
      {error ? <p className="text-xs text-red-300" data-testid={`respond-error-${c.reference}`}>{error}</p> : null}
      <button type="button" onClick={submit} className="px-2 py-1 rounded-md bg-sky-700 text-xs text-white"
        data-testid={`respond-submit-${c.reference}`}>Record the response</button>
    </div>
  );
}

export default function Complaints() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("open");
  const [error, setError] = useState("");
  const [open, setOpen] = useState("");
  const [note, setNote] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await axios.get(BASE, { headers: authHeaders(), params: { status: filter } });
      setData(r.data);
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load complaints"));
    }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  // Each call names its route in full, so the route check (contract gate) can match it.
  const act = async (url, body = {}) => {
    try {
      await axios.post(url, body, { headers: authHeaders() });
      setError("");
      await load();
    } catch (err) {
      setError(errorMessage(err, "That did not work"));
    }
  };

  if (!data && error) return <div className="p-6 text-sm text-red-300" data-testid="complaints-error">{error}</div>;
  if (!data) return <div className="p-6 text-sm text-slate-400">Loading complaints…</div>;

  return (
    <div className="p-6 max-w-5xl space-y-4" data-testid="complaints-page">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-slate-100">Complaints</h1>
        <select className="px-2 py-1 rounded-md text-sm bg-slate-900/60 border border-slate-700/60 text-slate-200"
          value={filter} onChange={(e) => setFilter(e.target.value)} id="complaints-filter">
          <option value="open">Open</option><option value="closed">Closed</option><option value="all">All</option>
        </select>
        <Link to="/complaints/new" className="ml-auto text-sm text-sky-300 underline">Report a complaint</Link>
      </header>
      {error ? <p className="text-sm text-red-300" data-testid="complaints-error">{error}</p> : null}
      {data.complaints.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="complaints-none">No {filter === "all" ? "" : filter} complaints.</p>
      ) : data.complaints.map((c) => (
        <article key={c.reference} id={c.reference} className="p-3 rounded-md border border-slate-700/60 bg-slate-900/40 space-y-2"
          data-testid={`complaint-${c.reference}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm text-slate-100">{c.reference}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300">{STATUS[c.status] || c.status}</span>
            {c.data_protection ? <span className="text-xs px-2 py-0.5 rounded-full bg-violet-900/50 text-violet-200">Data protection</span> : null}
            <span className="text-xs text-slate-400">{c.complainant_name}{c.matter_ref ? ` · ${c.matter_ref}` : ""} · received {when(c.received_at)}</span>
          </div>
          <p className="text-sm text-slate-200">{c.summary}</p>
          {c.communication_needs ? <p className="text-xs text-amber-200">Communication needs: {c.communication_needs}</p> : null}
          <Due label="Acknowledge (72 hours)" iso={c.acknowledge_by} done={c.acknowledged_at} />
          <Due label="Substantive response (21 days)" iso={c.respond_target} done={c.response?.sent_at} />
          <Due label="Resolve (8 weeks)" iso={c.resolve_by} done={c.closed_at} />
          {(c.updates || []).map((u) => <div key={u.at} className="text-xs text-slate-400">Update {when(u.at)}: {u.note}</div>)}
          {c.status !== "closed" ? (
            <div className="flex flex-wrap gap-2">
              {!c.acknowledged_at ? (
                <button type="button" className="px-2 py-1 rounded-md bg-sky-700 text-xs text-white"
                  onClick={() => act(`${BASE}/${c.reference}/acknowledge`)} data-testid={`ack-${c.reference}`}>Mark acknowledged</button>
              ) : null}
              {c.acknowledged_at && !c.response ? (
                <>
                  <input className="px-2 py-1 rounded-md text-xs bg-slate-900/60 border border-slate-700/60 text-slate-100"
                    placeholder="Progress update sent to the complainant" value={note[c.reference] || ""}
                    onChange={(e) => setNote({ ...note, [c.reference]: e.target.value })} id={`note-${c.reference}`} />
                  <button type="button" className="px-2 py-1 rounded-md border border-slate-600 text-xs text-slate-200"
                    onClick={() => act(`${BASE}/${c.reference}/update`, { note: note[c.reference] || "" })}>Record update</button>
                  <button type="button" className="px-2 py-1 rounded-md border border-slate-600 text-xs text-slate-200"
                    onClick={() => setOpen(open === c.reference ? "" : c.reference)} data-testid={`open-respond-${c.reference}`}>
                    Record the written response</button>
                </>
              ) : null}
              {c.response ? (
                <button type="button" className="px-2 py-1 rounded-md bg-emerald-700 text-xs text-white"
                  onClick={() => act(`${BASE}/${c.reference}/close`)} data-testid={`close-${c.reference}`}>Close the complaint</button>
              ) : null}
            </div>
          ) : null}
          {/* §6.6: further issues reopen a closed complaint; contact from the Legal Ombudsman
              reopens it, or marks an open one, as with the Ombudsman. */}
          {c.status !== "with_leo" ? (
            <div className="flex flex-wrap gap-2 items-center">
              <input className="px-2 py-1 rounded-md text-xs bg-slate-900/60 border border-slate-700/60 text-slate-100"
                placeholder="Reason" value={note[`r-${c.reference}`] || ""} id={`reason-${c.reference}`}
                onChange={(e) => setNote({ ...note, [`r-${c.reference}`]: e.target.value })} />
              {c.status === "closed" ? (
                <button type="button" className="px-2 py-1 rounded-md border border-slate-600 text-xs text-slate-200"
                  onClick={() => act(`${BASE}/${c.reference}/reopen`, { reason: note[`r-${c.reference}`] || "", legal_ombudsman: false })}
                  data-testid={`reopen-${c.reference}`}>Reopen: further issues raised</button>
              ) : null}
              <button type="button" className="px-2 py-1 rounded-md border border-amber-600 text-xs text-amber-200"
                onClick={() => act(`${BASE}/${c.reference}/reopen`, { reason: note[`r-${c.reference}`] || "", legal_ombudsman: true })}
                data-testid={`leo-${c.reference}`}>The Legal Ombudsman has been in touch</button>
            </div>
          ) : null}
          {open === c.reference ? (
            <RespondForm c={c} checklist={data.checklist} adr={data.adr_statement}
              onDone={() => { setOpen(""); load(); }} />
          ) : null}
        </article>
      ))}
    </div>
  );
}
